import React from 'react';

/**
 * Inline sparkline chart for trend cells. Renders a soft gradient fill plus a
 * stroke through the data points.
 *
 *   <Spark data={[78, 77.5, 77.2, 76.8, 76.4, 75.9]} color="var(--primary)" />
 *
 * Empty / single-point arrays render an empty SVG (so the caller can place
 * one unconditionally without a guard). Disable the gradient with
 * `fill={false}` for a denser look in tight cells.
 */
let sparkIdCounter = 0;

export default function Spark({
  data = [],
  color = 'var(--primary)',
  width = 120,
  height = 36,
  fill = true,
  strokeWidth = 2,
  className,
}) {
  if (!Array.isArray(data) || data.length < 2) {
    return <svg className={className} width={width} height={height} aria-hidden="true" />;
  }
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - ((v - min) / range) * (height - 4) - 2;
    return [x, y];
  });
  const path = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0]},${p[1]}`).join(' ');
  const area = `${path} L${width},${height} L0,${height} Z`;
  // Stable, monotonic id avoids a Math.random call per render.
  const id = `spark-${++sparkIdCounter}`;
  return (
    <svg
      className={`spark ${className || ''}`.trim()}
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      aria-hidden="true"
    >
      {fill ? (
        <defs>
          <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.25" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
      ) : null}
      {fill ? <path d={area} fill={`url(#${id})`} /> : null}
      <path
        d={path}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
